'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogState, AlertOpts, ConfirmOpts, PromptOpts } from './types';

interface DialogContextType {
  showAlert: (opts: AlertOpts) => Promise<void>;
  showConfirm: (opts: ConfirmOpts) => Promise<boolean>;
  showPrompt: (opts: PromptOpts) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | null>(null);

export function useDialogContext() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogProvider');
  }
  return context;
}

interface DialogProviderProps {
  children: ReactNode;
}

export function DialogProvider({ children }: DialogProviderProps) {
  const [dialogs, setDialogs] = useState<DialogState[]>([]);
  const [promptValue, setPromptValue] = useState('');
  const [promptError, setPromptError] = useState<string | null>(null);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const removeDialog = useCallback((id: string) => {
    setDialogs(prev => prev.filter(d => d.id !== id));
  }, []);

  const showAlert = useCallback((opts: AlertOpts): Promise<void> => {
    return new Promise((resolve, reject) => {
      const id = generateId();
      const dialog: DialogState = {
        id,
        type: 'alert',
        opts,
        resolve,
        reject
      };
      setDialogs(prev => [...prev, dialog]);
    });
  }, []);

  const showConfirm = useCallback((opts: ConfirmOpts): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const id = generateId();
      const dialog: DialogState = {
        id,
        type: 'confirm',
        opts,
        resolve,
        reject
      };
      setDialogs(prev => [...prev, dialog]);
    });
  }, []);

  const showPrompt = useCallback((opts: PromptOpts): Promise<string | null> => {
    return new Promise((resolve, reject) => {
      const id = generateId();
      const dialog: DialogState = {
        id,
        type: 'prompt',
        opts,
        resolve,
        reject
      };
      setDialogs(prev => [...prev, dialog]);
      setPromptValue(opts.initialValue || '');
      setPromptError(null);
    });
  }, []);

  const getVariantStyles = (variant?: string) => {
    switch (variant) {
      case 'destructive':
        return 'destructive';
      case 'success':
        return 'default';
      case 'warning':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handlePromptConfirm = (dialog: DialogState) => {
    const opts = dialog.opts as PromptOpts;
    
    // Validação required
    if (opts.required && !promptValue.trim()) {
      setPromptError('Este campo é obrigatório');
      return;
    }

    // Validação customizada
    if (opts.validate) {
      const error = opts.validate(promptValue);
      if (error) {
        setPromptError(error);
        return;
      }
    }

    dialog.resolve(promptValue);
    removeDialog(dialog.id);
    setPromptValue('');
    setPromptError(null);
  };

  const contextValue: DialogContextType = {
    showAlert,
    showConfirm,
    showPrompt
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      
      {dialogs.map(dialog => {
        if (dialog.type === 'alert') {
          const opts = dialog.opts as AlertOpts;
          return (
            <AlertDialog key={dialog.id} open={true}>
              <AlertDialogContent className="sm:max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle>{opts.title}</AlertDialogTitle>
                  {opts.description && (
                    <AlertDialogDescription>{opts.description}</AlertDialogDescription>
                  )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogAction
                    className={getVariantStyles(opts.variant)}
                    onClick={() => {
                      dialog.resolve(undefined);
                      removeDialog(dialog.id);
                    }}
                  >
                    {opts.okText || 'OK'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        }

        if (dialog.type === 'confirm') {
          const opts = dialog.opts as ConfirmOpts;
          const isDestructive = opts.variant === 'destructive';
          
          return (
            <AlertDialog key={dialog.id} open={true}>
              <AlertDialogContent className="sm:max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle>{opts.title}</AlertDialogTitle>
                  {opts.description && (
                    <AlertDialogDescription>{opts.description}</AlertDialogDescription>
                  )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    onClick={() => {
                      dialog.resolve(false);
                      removeDialog(dialog.id);
                    }}
                  >
                    {opts.cancelText || 'Cancelar'}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className={getVariantStyles(opts.variant)}
                    onClick={() => {
                      dialog.resolve(true);
                      removeDialog(dialog.id);
                    }}
                  >
                    {opts.confirmText || 'Confirmar'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        }

        if (dialog.type === 'prompt') {
          const opts = dialog.opts as PromptOpts;
          
          return (
            <Dialog key={dialog.id} open={true}>
              <DialogContent 
                className="sm:max-w-md"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => {
                  dialog.resolve(null);
                  removeDialog(dialog.id);
                  setPromptValue('');
                  setPromptError(null);
                }}
              >
                <DialogHeader>
                  <DialogTitle>{opts.title}</DialogTitle>
                  {opts.description && (
                    <DialogDescription>{opts.description}</DialogDescription>
                  )}
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="prompt-input">Valor</Label>
                    <Input
                      id="prompt-input"
                      value={promptValue}
                      onChange={(e) => {
                        setPromptValue(e.target.value);
                        setPromptError(null);
                      }}
                      placeholder={opts.placeholder}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handlePromptConfirm(dialog);
                        }
                      }}
                      autoFocus
                    />
                    {promptError && (
                      <p className="text-sm text-red-600">{promptError}</p>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      dialog.resolve(null);
                      removeDialog(dialog.id);
                      setPromptValue('');
                      setPromptError(null);
                    }}
                  >
                    {opts.cancelText || 'Cancelar'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => handlePromptConfirm(dialog)}
                  >
                    {opts.okText || 'OK'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          );
        }

        return null;
      })}
    </DialogContext.Provider>
  );
}